import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { ApiError, api, unwrap, type Schemas } from '../api/client.js';
import { PageTitle } from '../components/Layouts.js';
import { notScanned, scanError, useStartScan } from '../components/site.js';
import { Button, LinkButton, LoadError, Loading, Notice } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDateTime } from '../i18n.js';
import { useMe } from '../session.js';

type CrawlLink = Schemas['CrawlLink'];
type View = 'broken' | 'redirected' | 'internal' | 'external' | 'images';

const active = (scan: Schemas['Scan'] | undefined) =>
  scan?.status === 'queued' || scan?.status === 'running';

/** "/kontakt" for a page of the site, the host and path for another site. */
function shortUrl(url: string, internal: boolean) {
  try {
    const u = new URL(url);
    const path = `${u.pathname}${u.search}`;
    return internal ? path : `${u.host}${path === '/' ? '' : path}`;
  } catch {
    return url;
  }
}

/** One target with every page it was found on. */
interface Grouped {
  readonly link: CrawlLink;
  readonly sources: string[];
}

function group(items: readonly CrawlLink[]): Grouped[] {
  const byTarget = new Map<string, Grouped>();
  for (const l of items) {
    const found = byTarget.get(l.targetUrl);
    if (found) {
      if (!found.sources.includes(l.sourceUrl)) found.sources.push(l.sourceUrl);
    } else byTarget.set(l.targetUrl, { link: l, sources: [l.sourceUrl] });
  }
  return [...byTarget.values()];
}

/** The short error on the badge: the HTTP status or what the connection said. */
function badge(link: CrawlLink, t: TFunction) {
  if (link.httpStatus !== null) return String(link.httpStatus);
  return t(`links.badge.${link.errorCode ?? 'unknown'}`, {
    defaultValue: t('links.badge.unknown'),
  });
}

/** What to do about it, as a key of links.suggestions. */
function suggestion(link: CrawlLink): string {
  const status = link.httpStatus;
  if (link.errorCode === 'DNS_NXDOMAIN') return 'remove';
  if (status !== null && status >= 500) return 'server';
  if (status === 404 || status === 410) {
    if (link.kind === 'img') return 'image';
    if (link.kind !== 'a') return 'resource';
    return link.isInternal ? 'internal' : 'external';
  }
  return 'later';
}

/** The problem card behind a link, with its ready-made fix (T-72). */
function ruleOf(link: CrawlLink) {
  if (link.kind === 'img') return 'LINK_BROKEN_IMAGE';
  if (link.kind !== 'a') return 'LINK_BROKEN_RESOURCE';
  return link.isInternal ? 'LINK_BROKEN_INTERNAL' : 'LINK_BROKEN_EXTERNAL';
}

const BAR = [
  ['ok', 'bg-green-500'],
  ['redirected', 'bg-amber-500'],
  ['broken', 'bg-red-600'],
  ['unknown', 'bg-slate-300'],
] as const;

/** Links of the latest crawl (T-100, mock-up "Links: Tabelle mit Filtern"). */
export function Links() {
  const { siteId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const [view, setView] = useState<View>('broken');
  const scans = useQuery({
    queryKey: ['site', siteId, 'scans', 'crawl'],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/scans', {
          params: { path: { siteId }, query: { type: 'crawl', limit: 5 } },
        }),
      ),
    refetchInterval: (query) => (active(query.state.data?.items[0]) ? 5000 : false),
  });
  const running = scans.data?.items.find(active);
  // A crawl closed because the site was paused crawled nothing: the one before it still counts.
  const latest = scans.data?.items.find((s) => !active(s) && !notScanned(s));
  const links = useQuery({
    queryKey: ['site', siteId, 'links', latest?.id],
    queryFn: () =>
      unwrap(
        api.GET('/scans/{scanId}/links', {
          params: { path: { scanId: latest?.id ?? '' }, query: { broken: 'true', limit: 200 } },
        }),
      ),
    enabled: Boolean(latest),
  });
  // Redirects only when asked for: the page itself needs one list.
  const redirects = useQuery({
    queryKey: ['site', siteId, 'links', latest?.id, 'redirected'],
    queryFn: () =>
      unwrap(
        api.GET('/scans/{scanId}/links', {
          params: {
            path: { scanId: latest?.id ?? '' },
            query: { redirected: 'true', limit: 200 },
          },
        }),
      ),
    enabled: Boolean(latest) && view === 'redirected',
  });
  const start = useStartScan(siteId, 'crawl');
  const summary = links.data?.summary;
  const broken = group(links.data?.items ?? []);
  const subsets: Record<'internal' | 'external' | 'images', Grouped[]> = {
    internal: broken.filter((b) => b.link.isInternal && b.link.kind === 'a'),
    external: broken.filter((b) => !b.link.isInternal && b.link.kind === 'a'),
    images: broken.filter((b) => b.link.kind === 'img'),
  };
  const shown = view === 'broken' ? broken : view === 'redirected' ? [] : subsets[view];
  const source = (url: string) => {
    const path = shortUrl(url, true);
    return path === '/' ? t('links.homePage') : path;
  };
  const chips: [View, number | null][] = [
    ['broken', summary?.broken ?? null],
    ['redirected', summary?.redirected ?? null],
    ['internal', subsets.internal.length],
    ['external', subsets.external.length],
    ['images', subsets.images.length],
  ];

  return (
    <>
      <PageTitle>{t('links.title')}</PageTitle>
      <div className="mb-4 flex flex-col gap-3 empty:hidden">
        {start.isError ? (
          <Notice kind="error">
            {scanError(start.error, t)}{' '}
            {start.error instanceof ApiError && start.error.code === 'FORBIDDEN' ? (
              <LinkButton to={`/sites/${siteId}/settings`} variant="soft" size="sm">
                {t('scan.toSettings')}
              </LinkButton>
            ) : null}
          </Notice>
        ) : null}
        {running ? <Notice kind="info">{t('links.running')}</Notice> : null}
      </div>
      {scans.isPending ? <Loading /> : null}
      {scans.isError ? (
        <LoadError message={errorMessage(scans.error, t)} onRetry={() => void scans.refetch()} />
      ) : null}

      <section className="mb-4 flex flex-wrap items-center gap-6 rounded-xl border border-slate-200 bg-white p-5">
        <div className="w-full min-w-0 sm:w-auto sm:flex-[1_1_16rem]">
          {latest ? (
            <>
              <p className="text-sm text-slate-700">
                {t('links.lastRun', {
                  date: formatDateTime(
                    latest.finishedAt ?? latest.createdAt,
                    i18n.language,
                    timeZone,
                  ),
                  count: latest.pagesCrawled ?? 0,
                })}
              </p>
              <h2 className="mt-1 text-xl font-extrabold text-balance">
                {summary
                  ? summary.broken > 0
                    ? t('links.headline', { broken: summary.broken, count: summary.total })
                    : t('links.allFine', { count: summary.total })
                  : '…'}
              </h2>
              {latest.status !== 'completed' ? (
                <p className="mt-1 text-sm text-slate-700">
                  {t(`links.scanStatus.${latest.status}`)}
                </p>
              ) : null}
            </>
          ) : scans.data && !running ? (
            <p className="text-slate-800">{t('links.none')}</p>
          ) : null}
        </div>
        {summary && summary.total > 0 ? (
          <div className="w-full min-w-0 sm:w-auto sm:flex-[1_1_20rem]">
            <div
              role="img"
              aria-label={BAR.map(([k]) => t(`links.bar.${k}`, { count: summary[k] })).join(', ')}
              className="flex h-3.5 gap-0.5 overflow-hidden rounded-full"
            >
              {BAR.map(([k, color]) =>
                summary[k] > 0 ? (
                  <div key={k} className={color} style={{ flexGrow: summary[k] }} />
                ) : null,
              )}
            </div>
            <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-700">
              {BAR.map(([k]) =>
                summary[k] > 0 ? (
                  <li key={k} className={k === 'broken' ? 'font-semibold text-red-800' : ''}>
                    {t(`links.bar.${k}`, { count: summary[k] })}
                  </li>
                ) : null,
              )}
            </ul>
          </div>
        ) : null}
        <Button busy={start.isPending} disabled={Boolean(running)} onClick={() => start.mutate()}>
          {t('links.recheck')}
        </Button>
      </section>

      {links.isError ? (
        <LoadError message={errorMessage(links.error, t)} onRetry={() => void links.refetch()} />
      ) : null}

      {summary && summary.total > 0 ? (
        <>
          <div role="group" aria-label={t('links.filter')} className="mb-3 flex flex-wrap gap-2">
            {chips.map(([k, n]) => (
              <button
                key={k}
                type="button"
                aria-pressed={view === k}
                onClick={() => setView(k)}
                className={`min-h-10 rounded-full px-3.5 text-sm font-semibold ${view === k ? 'bg-[#0b1b3f] text-white' : 'border border-slate-300 bg-white text-slate-900 hover:bg-blue-50'}`}
              >
                {t(`links.views.${k}`)}
                {n !== null ? ` ${n}` : ''}
              </button>
            ))}
          </div>

          {view === 'redirected' ? (
            <RedirectTable
              rows={redirects.data ? group(redirects.data.items) : null}
              error={redirects.isError ? redirects.error : null}
              source={source}
              onRetry={() => void redirects.refetch()}
            />
          ) : shown.length === 0 ? (
            <Notice kind="success">
              {view === 'broken' ? t('links.noBroken') : t('links.noneHere')}
            </Notice>
          ) : (
            <div className="relative overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full min-w-[44rem] border-collapse text-sm">
                <caption className="sr-only">{t('links.broken', { count: shown.length })}</caption>
                <thead>
                  <tr className="bg-slate-50 text-left text-slate-700">
                    <th scope="col" className="px-4 py-3">
                      {t('links.columns.link')}
                    </th>
                    <th scope="col" className="px-4 py-3">
                      {t('links.columns.foundOn')}
                    </th>
                    <th scope="col" className="px-4 py-3">
                      {t('links.columns.error')}
                    </th>
                    <th scope="col" className="px-4 py-3">
                      {t('links.columns.suggestion')}
                    </th>
                    <th scope="col" className="px-4 py-3">
                      <span className="sr-only">{t('links.columns.fix')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map(({ link, sources }) => (
                    <tr key={link.id} className="border-t border-slate-100 align-top">
                      <td className="px-4 py-3.5">
                        <span className="font-semibold break-all">
                          {shortUrl(link.targetUrl, link.isInternal)}
                        </span>
                        <span className="block text-xs text-slate-600">
                          {t(`links.kinds.${link.kind}`)} ·{' '}
                          {link.isInternal ? t('links.internal') : t('links.external')}
                          {link.anchorText
                            ? ` · ${t('links.anchor', { text: link.anchorText })}`
                            : ''}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 break-words text-slate-700">
                        <Sources sources={sources} source={source} />
                      </td>
                      <td className="px-4 py-3.5">
                        <span
                          className="inline-block rounded bg-red-100 px-2 py-0.5 font-bold whitespace-nowrap text-red-900"
                          title={
                            link.httpStatus === null
                              ? t(`links.errors.${link.errorCode ?? 'unknown'}`, {
                                  defaultValue: t('links.errors.unknown'),
                                })
                              : undefined
                          }
                        >
                          {badge(link, t)}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">{t(`links.suggestions.${suggestion(link)}`)}</td>
                      <td className="px-4 py-3.5 text-right">
                        <Link
                          to={`/sites/${siteId}/findings?open=${ruleOf(link)}`}
                          className="font-semibold whitespace-nowrap text-blue-700 hover:underline"
                        >
                          {t('links.fix')}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </>
  );
}

/** Up to three source pages, then how many more. */
function Sources({ sources, source }: { sources: string[]; source: (url: string) => string }) {
  const { t } = useTranslation();
  return (
    <>
      {sources.slice(0, 3).map(source).join(', ')}
      {sources.length > 3 ? ` ${t('links.morePages', { count: sources.length - 3 })}` : ''}
    </>
  );
}

/** Working links that answer only after a redirect: worth pointing straight at the target. */
function RedirectTable({
  rows,
  error,
  source,
  onRetry,
}: {
  rows: Grouped[] | null;
  error: unknown;
  source: (url: string) => string;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  if (error) return <LoadError message={errorMessage(error, t)} onRetry={onRetry} />;
  if (!rows) return <Loading />;
  if (rows.length === 0) return <Notice kind="success">{t('links.noRedirects')}</Notice>;
  return (
    <div className="relative overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <p className="px-4 pt-3 text-sm text-slate-700">{t('links.redirectHint')}</p>
      <table className="w-full min-w-[36rem] border-collapse text-sm">
        <caption className="sr-only">{t('links.views.redirected')}</caption>
        <thead>
          <tr className="text-left text-slate-700">
            <th scope="col" className="px-4 py-3">
              {t('links.columns.link')}
            </th>
            <th scope="col" className="px-4 py-3">
              {t('links.columns.foundOn')}
            </th>
            <th scope="col" className="px-4 py-3">
              {t('links.columns.redirects')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ link, sources }) => (
            <tr key={link.id} className="border-t border-slate-100 align-top">
              <td className="px-4 py-3.5 font-semibold break-all">
                {shortUrl(link.targetUrl, link.isInternal)}
              </td>
              <td className="px-4 py-3.5 break-words text-slate-700">
                <Sources sources={sources} source={source} />
              </td>
              <td className="px-4 py-3.5">
                <span className="inline-block rounded bg-amber-100 px-2 py-0.5 font-bold text-amber-950">
                  {t('links.hops', { count: link.redirectHops })}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
